import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import styled from 'styled-components';
import { getAllCollectionsAsync } from '../utils/inventoryService';
import PaymentModal from './PaymentModal';
import FavoriteButton from './FavoriteButton';
import LazyImage from './LazyImage';

const PageContainer = styled.div`
  padding: 40px 0;
`;

const LoadingContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 400px;
  color: #ccc;
  font-size: 1.2rem;
`;

const ErrorContainer = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  min-height: 400px;
  color: #ff6b6b;
  font-size: 1rem;
  gap: 15px;
`;

const RetryButton = styled.button`
  background: #333;
  color: #fff;
  padding: 10px 20px;
  border-radius: 3px;
  border: none;
  cursor: pointer;
  transition: background 0.2s ease-in-out;

  &:hover {
    background: #555;
  }
`;

const PageHeader = styled.h1`
  font-size: 2rem;
  margin-bottom: 30px;
  position: sticky;
  top: 180px;
  background-color: #000;
  z-index: 100;
  padding: 15px 0;
  margin-top: -15px;

  @media (max-width: 768px) {
    top: 140px;
  }
`;

const AvailableGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
  gap: 30px;
  margin-bottom: 40px;

  @media (max-width: 768px) {
    grid-template-columns: repeat(auto-fill, minmax(240px, 1fr));
    gap: 20px;
  }
`;

const ArtworkCard = styled.div`
  display: flex;
  flex-direction: column;
  background: rgba(255, 255, 255, 0.05);
  border-radius: 8px;
  overflow: hidden;
  transition: transform 0.3s ease;

  &:hover {
    transform: translateY(-5px);
  }
`;

const ArtworkImageContainer = styled.div`
  position: relative;
  aspect-ratio: 1;
  overflow: hidden;
`;

const ArtworkImage = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
  transition: transform 0.3s ease;

  ${ArtworkCard}:hover & {
    transform: scale(1.05);
  }
`;

const ArtworkDetails = styled.div`
  padding: 15px;
  flex-grow: 1;
  display: flex;
  flex-direction: column;
`;

const ArtworkTitle = styled.h3`
  font-size: 1.1rem;
  margin-bottom: 5px;
`;

const CollectionName = styled(Link)`
  font-size: 0.8rem;
  margin-bottom: 10px;
  color: #aaa;

  &:hover {
    color: #fff;
  }
`;

const ArtworkInfo = styled.div`
  margin-bottom: 10px;
  flex-grow: 1;
`;

const ArtworkDetail = styled.div`
  font-size: 0.9rem;
  color: #ddd;
  margin-bottom: 5px;
`;

const ArtworkPrice = styled.div`
  font-size: 1.2rem;
  font-weight: bold;
  margin: 10px 0;
`;

const PurchaseButton = styled.button`
  background: #2c7d46;
  color: white;
  border: none;
  padding: 10px 20px;
  border-radius: 4px;
  font-weight: bold;
  cursor: pointer;
  transition: background 0.2s ease;
  width: 100%;

  &:hover {
    background: #36964f;
  }
`;

const FilterContainer = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 15px;
  margin-bottom: 20px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
  padding-bottom: 20px;
`;

const FilterSectionTitle = styled.div`
  font-size: 1rem;
  font-weight: bold;
  margin-bottom: 10px;
  width: 100%;
`;

const FilterButton = styled.button`
  background: ${props => props.active ? 'rgba(255, 255, 255, 0.2)' : 'transparent'};
  border: 1px solid rgba(255, 255, 255, 0.3);
  color: white;
  padding: 8px 15px;
  border-radius: 4px;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    background: rgba(255, 255, 255, 0.1);
  }
`;

const PriceFilterContainer = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  margin-top: 15px;
  margin-bottom: 15px;
`;

const RangeSliderContainer = styled.div`
  display: flex;
  align-items: center;
  margin-top: 10px;
  gap: 15px;
`;

const RangeSlider = styled.input`
  -webkit-appearance: none;
  width: 100%;
  height: 4px;
  background: rgba(255, 255, 255, 0.2);
  border-radius: 2px;
  outline: none;

  &::-webkit-slider-thumb {
    -webkit-appearance: none;
    appearance: none;
    width: 16px;
    height: 16px;
    background: #2c7d46;
    border-radius: 50%;
    cursor: pointer;
  }

  &::-moz-range-thumb {
    width: 16px;
    height: 16px;
    background: #2c7d46;
    border-radius: 50%;
    cursor: pointer;
  }
`;

const PriceValue = styled.div`
  min-width: 80px;
  text-align: center;
  font-weight: bold;
`;

const PriceFilterButtons = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  margin-top: 15px;
`;

const ApplyFiltersButton = styled.button`
  background: #2c7d46;
  color: white;
  border: none;
  padding: 8px 15px;
  border-radius: 4px;
  font-weight: bold;
  cursor: pointer;
  transition: background 0.2s ease;
  margin-top: 15px;
  align-self: flex-start;

  &:hover {
    background: #36964f;
  }
`;

const NoResultsMessage = styled.div`
  padding: 20px;
  text-align: center;
  font-size: 1.1rem;
  color: #aaa;
`;

const SuccessMessage = styled.div`
  margin-top: 20px;
  padding: 15px;
  background-color: #2c7d46;
  color: white;
  border-radius: 4px;
  text-align: center;
  font-weight: bold;
`;

const SortContainer = styled.div`
  display: flex;
  justify-content: flex-end;
  margin-bottom: 20px;
  align-items: center;
`;

const SortLabel = styled.span`
  margin-right: 10px;
  font-size: 0.9rem;
  color: #aaa;
`;

const SortSelect = styled.select`
  background: transparent;
  border: 1px solid rgba(255, 255, 255, 0.3);
  color: white;
  padding: 8px 15px;
  border-radius: 4px;
  cursor: pointer;

  &:focus {
    outline: none;
  }
`;

const FilterResultsCount = styled.div`
  margin-bottom: 20px;
  font-size: 0.9rem;
  color: #aaa;
`;

function AvailableForSale() {
  const [availableItems, setAvailableItems] = useState([]);
  const [filteredItems, setFilteredItems] = useState([]);
  const [selectedMediumFilter, setSelectedMediumFilter] = useState('all');
  const [selectedItem, setSelectedItem] = useState(null);
  const [isCheckingOut, setIsCheckingOut] = useState(false);
  const [paymentComplete, setPaymentComplete] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Price filter states
  const [priceRange, setPriceRange] = useState([0, 10000]);
  const [currentPriceRange, setCurrentPriceRange] = useState([0, 10000]);
  const [sortOrder, setSortOrder] = useState('default');

  // Get all available items
  useEffect(() => {
    let mounted = true;

    const loadItems = async () => {
      try {
        setLoading(true);
        setError(null);

        const collections = await getAllCollectionsAsync();

        // Extract all available items from all collections
        const items = [];

        collections.forEach(collection => {
          if (collection.items && Array.isArray(collection.items)) {
            collection.items.forEach(item => {
              if (item.available && item.price) {
                // Add collection info to each item
                items.push({
                  ...item,
                  collectionId: collection.id,
                  collectionTitle: collection.title,
                  collectionFolder: collection.folder
                });
              }
            });
          }
        });

        if (!mounted) return;

        // Find min and max prices
        if (items.length > 0) {
          const prices = items.map(item => item.price);
          const minPrice = Math.floor(Math.min(...prices));
          const maxPrice = Math.ceil(Math.max(...prices));
          setPriceRange([minPrice, maxPrice]);
          setCurrentPriceRange([minPrice, maxPrice]);
        }

        setAvailableItems(items);
        setFilteredItems(items);
      } catch (err) {
        console.error('Error loading available items:', err);
        if (mounted) {
          setError('Failed to load available items. Please try again.');
        }
      } finally {
        if (mounted) {
          setLoading(false);
        }
      }
    };

    loadItems();

    return () => {
      mounted = false;
    };
  }, []);

  // Generate price brackets for quick selection
  const generatePriceBrackets = () => {
    if (availableItems.length === 0) return [];

    const brackets = [
      { min: 0, max: 500, label: 'Under $500' },
      { min: 500, max: 1000, label: '$500 - $1,000' },
      { min: 1000, max: 2500, label: '$1,000 - $2,500' },
      { min: 2500, max: 5000, label: '$2,500 - $5,000' },
      { min: 5000, max: 10000, label: '$5,000 - $10,000' },
      { min: 10000, max: priceRange[1] + 1, label: 'Over $10,000' }
    ];

    // Only return brackets that have items
    return brackets.filter(bracket => {
      return availableItems.some(item =>
        item.price >= bracket.min && item.price < bracket.max
      );
    });
  };

  // Apply all filters (medium and price)
  const applyFilters = () => {
    let filtered = [...availableItems];

    // Apply medium filter
    if (selectedMediumFilter !== 'all') {
      filtered = filtered.filter(item => {
        const medium = item.medium ? item.medium.toLowerCase() : '';

        switch(selectedMediumFilter) {
          case 'encaustic':
            return medium.includes('encaustic');
          case 'acrylic-canvas':
            return medium.includes('acrylic on canvas');
          case 'acrylic-paper':
            return medium.includes('acrylic on paper');
          case 'acrylic-pen-canvas':
            return medium.includes('acrylic pen on canvas');
          case 'acrylic-pen-cloth':
            return medium.includes('acrylic pen on cloth');
          default:
            return true;
        }
      });
    }

    // Apply price filter
    filtered = filtered.filter(item =>
      item.price >= currentPriceRange[0] && item.price <= currentPriceRange[1]
    );

    // Apply sorting
    if (sortOrder !== 'default') {
      filtered.sort((a, b) => {
        if (sortOrder === 'price-low-high') {
          return a.price - b.price;
        } else if (sortOrder === 'price-high-low') {
          return b.price - a.price;
        }
        return 0;
      });
    }

    setFilteredItems(filtered);
  };

  // Update filters whenever any filter changes
  useEffect(() => {
    applyFilters();
  }, [selectedMediumFilter, currentPriceRange, sortOrder]);

  // Filter by medium type
  const filterByMedium = (filter) => {
    setSelectedMediumFilter(filter);
  };

  // Handle price range selection from quick brackets
  const handlePriceBracketClick = (min, max) => {
    setCurrentPriceRange([min, max]);
  };

  // Handle min price slider change
  const handleMinPriceChange = (e) => {
    const newMin = parseInt(e.target.value);
    if (newMin <= currentPriceRange[1]) {
      setCurrentPriceRange([newMin, currentPriceRange[1]]);
    }
  };

  // Handle max price slider change
  const handleMaxPriceChange = (e) => {
    const newMax = parseInt(e.target.value);
    if (newMax >= currentPriceRange[0]) {
      setCurrentPriceRange([currentPriceRange[0], newMax]);
    }
  };

  // Handle sort order change
  const handleSortChange = (e) => {
    setSortOrder(e.target.value);
  };

  // Define medium types and their display names
  const mediumTypes = [
    { id: 'encaustic', name: 'Encaustic' },
    { id: 'acrylic-canvas', name: 'Acrylic on Canvas' },
    { id: 'acrylic-paper', name: 'Acrylic on Paper' },
    { id: 'acrylic-pen-canvas', name: 'Acrylic Pen on Canvas' },
    { id: 'acrylic-pen-cloth', name: 'Acrylic Pen on Cloth' }
  ];

  // Get available mediums for filter buttons
  const getAvailableMediums = () => {
    // Check which medium types are actually present in available items
    return mediumTypes.filter(mediumType => {
      return availableItems.some(item => {
        const medium = item.medium ? item.medium.toLowerCase() : '';

        switch(mediumType.id) {
          case 'encaustic':
            return medium.includes('encaustic');
          case 'acrylic-canvas':
            return medium.includes('acrylic on canvas');
          case 'acrylic-paper':
            return medium.includes('acrylic on paper');
          case 'acrylic-pen-canvas':
            return medium.includes('acrylic pen on canvas');
          case 'acrylic-pen-cloth':
            return medium.includes('acrylic pen on cloth');
          default:
            return false;
        }
      });
    });
  };

  // Handle purchase button click
  const handlePurchase = (item) => {
    setSelectedItem(item);
    setIsCheckingOut(true);
  };

  // Handle payment success
  const handlePaymentSuccess = async () => {
    setPaymentComplete(true);
    setIsCheckingOut(false);

    // Reload items to reflect the purchased item as unavailable
    try {
      const collections = await getAllCollectionsAsync();

      // Extract all available items from all collections
      const items = [];

      collections.forEach(collection => {
        if (collection.items && Array.isArray(collection.items)) {
          collection.items.forEach(item => {
            if (item.available && item.price) {
              // Add collection info to each item
              items.push({
                ...item,
                collectionId: collection.id,
                collectionTitle: collection.title,
                collectionFolder: collection.folder
              });
            }
          });
        }
      });

      // Update price range if needed
      if (items.length > 0) {
        const prices = items.map(item => item.price);
        const minPrice = Math.floor(Math.min(...prices));
        const maxPrice = Math.ceil(Math.max(...prices));
        setPriceRange([minPrice, maxPrice]);
        setCurrentPriceRange([minPrice, maxPrice]);
      }

      setAvailableItems(items);
      setFilteredItems(items);
      console.log('✅ Available items refreshed after purchase');
    } catch (err) {
      console.error('Error refreshing available items:', err);
    }
  };

  // Close payment modal
  const closePaymentModal = () => {
    setIsCheckingOut(false);
    setSelectedItem(null);
  };

  // Get image path for an item
  const getImagePath = (item) => {
    // Items can have either 'image' or 'imagePath' property
    const imagePath = item.image || item.imagePath;

    if (!imagePath) {
      console.warn('Missing image path for item:', item.id);
      return '';
    }

    // If it's already a full path, use it directly
    if (imagePath.includes('/')) {
      return imagePath;
    }

    // Otherwise build the path
    return `/images/collections/${item.collectionFolder}/${imagePath}`;
  };

  // Format price for display
  const formatPrice = (price) => {
    return price.toLocaleString('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    });
  };

  const handleRetry = () => {
    window.location.reload();
  };

  if (loading) {
    return (
      <PageContainer>
        <LoadingContainer>Loading available artwork...</LoadingContainer>
      </PageContainer>
    );
  }

  if (error) {
    return (
      <PageContainer>
        <ErrorContainer>
          <div>{error}</div>
          <RetryButton onClick={handleRetry}>Retry</RetryButton>
        </ErrorContainer>
      </PageContainer>
    );
  }

  return (
    <PageContainer>
      <PageHeader>Available for Sale</PageHeader>

      <FilterContainer>
        <FilterSectionTitle>Filter by Medium</FilterSectionTitle>
        <FilterButton
          active={selectedMediumFilter === 'all'}
          onClick={() => filterByMedium('all')}
        >
          All Mediums
        </FilterButton>

        {getAvailableMediums().map(medium => (
          <FilterButton
            key={medium.id}
            active={selectedMediumFilter === medium.id}
            onClick={() => filterByMedium(medium.id)}
          >
            {medium.name}
          </FilterButton>
        ))}

        <PriceFilterContainer>
          <FilterSectionTitle>Filter by Price</FilterSectionTitle>

          <PriceFilterButtons>
            {generatePriceBrackets().map((bracket, index) => (
              <FilterButton
                key={index}
                active={currentPriceRange[0] === bracket.min && currentPriceRange[1] === bracket.max}
                onClick={() => handlePriceBracketClick(bracket.min, bracket.max)}
              >
                {bracket.label}
              </FilterButton>
            ))}
          </PriceFilterButtons>

          <RangeSliderContainer>
            <PriceValue>{formatPrice(currentPriceRange[0])}</PriceValue>
            <RangeSlider
              type="range"
              min={priceRange[0]}
              max={priceRange[1]}
              step={100}
              value={currentPriceRange[0]}
              onChange={handleMinPriceChange}
            />
            <RangeSlider
              type="range"
              min={priceRange[0]}
              max={priceRange[1]}
              step={100}
              value={currentPriceRange[1]}
              onChange={handleMaxPriceChange}
            />
            <PriceValue>{formatPrice(currentPriceRange[1])}</PriceValue>
          </RangeSliderContainer>
        </PriceFilterContainer>
      </FilterContainer>

      <SortContainer>
        <SortLabel>Sort by:</SortLabel>
        <SortSelect value={sortOrder} onChange={handleSortChange}>
          <option value="default">Featured</option>
          <option value="price-low-high">Price: Low to High</option>
          <option value="price-high-low">Price: High to Low</option>
        </SortSelect>
      </SortContainer>

      <FilterResultsCount>
        Showing {filteredItems.length} of {availableItems.length} artworks
      </FilterResultsCount>

      {filteredItems.length === 0 ? (
        <NoResultsMessage>
          No available artworks found with the selected filters. Please adjust your filters to see more options.
        </NoResultsMessage>
      ) : (
        <AvailableGrid>
          {filteredItems.map(item => (
            <ArtworkCard key={item.id}>
              <ArtworkImageContainer>
                <LazyImage src={getImagePath(item)} alt={item.title} hoverEffect={true} />
                <FavoriteButton item={item} />
              </ArtworkImageContainer>

              <ArtworkDetails>
                <ArtworkTitle>{item.title}</ArtworkTitle>
                <CollectionName to={`/collection/${item.collectionId}`}>
                  {item.collectionTitle}
                </CollectionName>

                <ArtworkInfo>
                  {item.medium && (
                    <ArtworkDetail><strong>Medium:</strong> {item.medium}</ArtworkDetail>
                  )}
                  {item.size && (
                    <ArtworkDetail><strong>Size:</strong> {item.size}</ArtworkDetail>
                  )}
                  {item.year && (
                    <ArtworkDetail><strong>Year:</strong> {item.year}</ArtworkDetail>
                  )}
                </ArtworkInfo>

                <ArtworkPrice>{formatPrice(item.price)}</ArtworkPrice>
                <PurchaseButton onClick={() => handlePurchase(item)}>
                  Purchase Now
                </PurchaseButton>
              </ArtworkDetails>
            </ArtworkCard>
          ))}
        </AvailableGrid>
      )}

      {isCheckingOut && selectedItem && (
        <PaymentModal
          artwork={selectedItem}
          onClose={closePaymentModal}
          onSuccess={handlePaymentSuccess}
        />
      )}

      {paymentComplete && (
        <SuccessMessage>
          Thank you for your purchase! We will contact you shortly to arrange delivery.
        </SuccessMessage>
      )}
    </PageContainer>
  );
}

export default AvailableForSale;